/**
 * Switches a person turns on for their own browser, for designs that are landing in pieces.
 *
 * Both are presentation only: no server flag, no row to migrate, nothing to coordinate with a
 * deployment. A deploy carrying this code shows the current console to everyone who has not asked
 * for the other one, which is what makes it safe to ship an approved design one slice at a time
 * instead of in one release nobody can take back.
 *
 * They are separate on purpose. The menu switch (TICK-120) changes where things are; the palette
 * (TICK-130) changes what they look like. Someone reviewing the design should be able to say the
 * grouping helps and the colour does not, or the reverse, without the two arriving welded together.
 *
 * Every read is wrapped: a browser with site data blocked throws on the accessor itself, and neither
 * of these is worth a blank page.
 */

type WebStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;

// The owner-selected Staging V2 release candidate has one console presentation. Ignore old
// query switches and remembered preferences there; other deployments retain their rollout rules.
const STAGING_CONSOLE_ORIGIN = 'https://jamtick-api-staging.k-suthichet.workers.dev';
const currentOrigin = () => typeof window === 'undefined' ? '' : window.location.origin;

function readFlag(
  param: string,
  onValue: string,
  offValue: string,
  storageKey: string,
  search: string,
  storage: WebStorage,
): boolean {
  const asked = new URLSearchParams(search).get(param);
  try {
    if (asked === onValue) storage?.setItem(storageKey, '1');
    if (asked === offValue) storage?.removeItem(storageKey);
    if (asked === onValue) return true;
    if (asked === offValue) return false;
    return storage?.getItem(storageKey) === '1';
  } catch {
    return asked === onValue;
  }
}

/**
 * The Event Admin menu's shape. Three values, because the design landed in two steps and the first
 * one is still the thing the second is judged against.
 *
 * - `classic` — the two groups the console shipped with.
 * - `lifecycle` — TICK-120: the same items, grouped by where the organizer is in the event's life.
 * - `phases` — the approved site map: six destinations, every other screen a section tab inside one
 *   of them. `lifecycle` grouped twenty buttons; this one leaves six.
 *
 * `lifecycle` is Event Admin only and SuperAdmin reads it as `classic`; `phases` covers both, so a
 * person reviewing the shape gets one answer from the whole product rather than half of it.
 */
export type NavMode = 'classic' | 'lifecycle' | 'phases';

const NAV_MODES: readonly NavMode[] = ['classic', 'lifecycle', 'phases'];
const NAV_KEY = 'jamtick.nav.mode';
/** The flag's first shape, when it was a lifecycle-or-not boolean. Read so a browser already opted
 *  into that menu keeps it across this deploy; written by nothing, and cleared by `?nav=classic`. */
const NAV_KEY_V1 = 'jamtick.nav.lifecycle';

const isNavMode = (x: string | null | undefined): x is NavMode => !!x && NAV_MODES.includes(x as NavMode);

/** `?nav=lifecycle` / `?nav=phases` pick a menu shape and remember it; `?nav=classic` restores. */
export function readNavMode(search: string, storage: WebStorage, origin = currentOrigin()): NavMode {
  if (origin === STAGING_CONSOLE_ORIGIN) return 'phases';
  const asked = new URLSearchParams(search).get('nav');
  try {
    if (asked === 'classic') {
      storage?.removeItem(NAV_KEY);
      storage?.removeItem(NAV_KEY_V1);
      return 'classic';
    }
    if (isNavMode(asked)) {
      storage?.setItem(NAV_KEY, asked);
      return asked;
    }
    const saved = storage?.getItem(NAV_KEY);
    if (isNavMode(saved)) return saved;
    return storage?.getItem(NAV_KEY_V1) === '1' ? 'lifecycle' : 'classic';
  } catch {
    return isNavMode(asked) ? asked : 'classic';
  }
}

/**
 * `?theme=unified` paints the organizer consoles in the approved palette; `?theme=classic` restores.
 *
 * Scoped to the consoles by design: the four approved screens are all organizer surfaces, and there
 * is no approved design for the storefront in this palette. Repainting the pages that take money
 * from a mockup nobody drew would be inventing, and the buyer's side is not where a colour
 * experiment belongs.
 */
export function readThemeMode(search: string, storage: WebStorage): 'classic' | 'unified' {
  return readFlag('theme', 'unified', 'classic', 'jamtick.theme.unified', search, storage)
    ? 'unified'
    : 'classic';
}

/**
 * TICK-140 — `?ui=v2` renders the organizer consoles in the redesigned shell; `?ui=v1` restores.
 *
 * The third switch, and the same reasoning as the first two: this design lands in slices, and a
 * deploy carrying it has to show the current console to everyone who has not asked for the other
 * one. Production organizers are selling while this is being built.
 *
 * It is separate from `theme` on purpose, as `nav` and `theme` are separate from each other. The
 * palette switch repaints the console that exists; this one replaces its shell, header, navigation
 * and state handling. Someone reviewing the redesign has to be able to say the new shell helps and
 * the new palette does not, which they cannot do if the two arrive welded together.
 */
export function readConsoleMode(search: string, storage: WebStorage, origin = currentOrigin()): 'v1' | 'v2' {
  if (origin === STAGING_CONSOLE_ORIGIN) return 'v2';
  return readFlag('ui', 'v2', 'v1', 'jamtick.console.v2', search, storage) ? 'v2' : 'v1';
}

/**
 * `?console-theme=light` renders the v2 console in the Figma Preview Light mode.
 *
 * Dark is the production direction and the default; light exists so the same component tree can be
 * checked in both. It is deliberately not tied to `prefers-color-scheme`: this is a review control
 * for a theme that has not shipped, and inheriting it from the operating system would hand an
 * unfinished palette to anyone whose laptop happens to be in light mode.
 */
export function readConsoleTheme(search: string, storage: WebStorage): 'dark' | 'light' {
  return readFlag('console-theme', 'light', 'dark', 'jamtick.console.light', search, storage)
    ? 'light'
    : 'dark';
}
